import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown, ChevronLeft, ChevronUp, Check, Download, FastForward,
  Inbox, GitBranch, Loader2, Pause, Play, RotateCcw, SkipForward, Square, Upload, Zap, Activity,
} from "lucide-react";
import { FeedCol } from "./FeedCol";
import { SpineCol, type SpineCardEntry, type SpineMatter } from "./SpineCol";
import { EvalPanel } from "./EvalPanel";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { CHECK_IDS, BASE_CHECK_MAP, LEGACY_AGENT_ALIAS, synthResult, meterCheck } from "../../checks";
import { SPEEDS } from "../../constants";
import { accumulateDocumentRegister, buildAddressRoster, cloneJSON, cvar, fmtDate } from "../../utils";
import {
  clearTrayScope, getBundleV2, listTray, recordRuling, resolveTrayItem, streamExecute,
  type ExecCheckRecord, type ExecResult, type TrayItem,
} from "../../api/workbench";
import type { LiveOrchestratorState } from "../../api/triage";
import { RunResultPanel, type RunPanelData } from "../../workbench/RunResultPanel";
import type { BundleMapV2 } from "../../workbench/types";
import { cancelDebounced, debounced, deleteRunState, saveRunState } from "../../api/client";
import {
  compareForecast, corpusExportUrl, deleteComparison, deleteForecast, getComparison, listForecasts,
} from "../../api/forecast";
import { extractActualSnapshots } from "../../forecast/extract";
import type { ForecastComparisonDoc, ForecastRunSummary } from "../../forecast/types";
import { ForecastPanel } from "./ForecastPanel";
import { ForecastUploadModal } from "./ForecastUploadModal";
import { computeEval, computeMemoMatch, buildMemosFromEval } from "../../eval";
import { computeAlignment, computePerCheck, computeTrackerMatch } from "../../evalContracts";
import type {
  ActionLog, Approval, CheckMap, CompletedRun, CorpusSet, CostState,
  CostUsageEntry, EvalSet, MemosMap, ProcessedEntry,
  RunStateSnapshot, SimCheck, SimEmail, SimReplay, Workspace,
  WorkstreamLite,
} from "../../types";

/* A run aborted via the Stop button surfaces as an AbortError / "cancelled" —
   distinguish it from a genuine failure so Stop doesn't show an error banner. */
function isCancelError(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof Error) return e.name === "AbortError" || /cancel/i.test(e.message);
  return false;
}

export interface PinnedVersion {
  bundleId: string;
  versionId: string;
  label: string;
}

export interface SimViewProps {
  workspace: Workspace;
  corpus: CorpusSet;
  /** The bundle version this workspace runs — execution reads it server-side. */
  pinned: PinnedVersion;
  versionOpts: Array<{ value: string; short: string }>;
  selVer: string;
  onChangeCheckVer: (value: string) => void;
  selEval: EvalSet | null;
  mattersInScope: WorkstreamLite[];
  wsName: (id: string) => string;
  wsNameMap: Record<string, string>;
  initialSnapshot: RunStateSnapshot | null;
  speedIdx: number;
  setSpeedIdx: (i: number) => void;
  onBack: () => void;
  onComplete: (workspaceId: string, run: CompletedRun) => void;
  onActiveWsChange?: (id: string) => void;
  /* Imports any corpus email still missing a backend serverId and resolves to
     localId → serverId for every synced email (null if the sync failed). */
  onEnsureSynced?: () => Promise<Record<number, string> | null>;
}

interface CostEntryDelta {
  id: string;
  inTok: number;
  outTok: number;
  cost: number;
  model: string;
  calls?: number;
  latency?: string | number;
}

interface EntryResult {
  entry: ProcessedEntry;
  apprs: Approval[];
  acts: ActionLog[];
  emailCost: number;
  emailTok: number;
  usage: CostEntryDelta[];
  liveMemos?: MemosMap;
}

interface LiveProgress {
  emailId: number;
  running: string[];
  done: string[];
}

/* Map seed agent ids to the legacy approval type names the eval scorer reads. */
const AGENT_EVAL_TYPE: Record<string, string> = {
  conflict: "conflict", newpart: "new_participant", phase: "phase",
  anomaly: "anomaly", privilege: "privilege", catalog: "routing", fundsflow: "funds_flow",
};

/* Adapt a v2 agent map into the legacy CheckMap shape the replay-era
   components (SpineCol names, ReplayView, forecast extractor) render. */
function legacyMapFromAgents(agents: BundleMapV2): CheckMap {
  const out: CheckMap = cloneJSON(BASE_CHECK_MAP);
  for (const [id, a] of Object.entries(agents)) {
    out[id] = {
      name: a.name, desc: a.desc, prompt: a.prompt, tau: a.tau, model: a.model,
      outTok: a.maxTokens, external: false, threshold_rationale: "",
      kind: a.role === "cleaner" || a.role === "router" ? "spine" : a.dest?.tracker || a.dest?.sa ? "workstream" : (BASE_CHECK_MAP[id]?.kind ?? "workstream"),
    };
  }
  return out;
}

/* The workspace view: owns the per-run state (cursor, processed, tray, memos,
   cost), the run loop, the executor-driven live path, and persistence of the
   in-progress snapshot. Left-to-right: Feed / Spine / run-result panel. */
export function SimView(props: SimViewProps): JSX.Element {
  const {
    workspace, corpus, pinned, versionOpts, selVer, onChangeCheckVer, selEval,
    mattersInScope, wsName, wsNameMap, initialSnapshot, speedIdx, setSpeedIdx,
    onBack, onComplete, onActiveWsChange, onEnsureSynced,
  } = props;
  const { isCompact, isMobile } = useBreakpoint();

  const emails = corpus.emails;
  const total = emails.length;
  const runScope = workspace.id;

  /* The bundle/version this run is executing against, snapshotted when the run
     starts. The dropdown stays changeable mid-run, but execution, the agent
     names/τ gates rendered from it, and the version recorded on the completed run
     all read this snapshot — so a mid-run change affects only the NEXT run and can
     never label a run with a bundle it did not use.
     `runPinnedRef` mirrors it for synchronous reads: step() snapshots and then
     immediately awaits processOne, before a state update would be visible. */
  const [execPin, setExecPinState] = useState<PinnedVersion>(pinned);
  const runPinnedRef = useRef<PinnedVersion>(pinned);
  const setExecPin = useCallback((p: PinnedVersion): void => {
    runPinnedRef.current = p;
    setExecPinState(p);
  }, []);

  /* The snapshot's agents — names, τ, roles. Keyed on the snapshot, not the live
     prop, so no refetch (and no transient `agents === null`) happens mid-run. */
  const [agents, setAgents] = useState<BundleMapV2 | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setAgents(null);
    setPinError(null);
    void getBundleV2(execPin.bundleId).then((d) => {
      if (!alive) return;
      const snap = d?.versions.find((v) => v.id === execPin.versionId)?.snap;
      if (snap) setAgents(snap);
      else setPinError("the pinned bundle version no longer exists — pick another from the dropdown");
    });
    return () => {
      alive = false;
    };
  }, [execPin.bundleId, execPin.versionId]);
  const CM: CheckMap = useMemo(() => (agents ? legacyMapFromAgents(agents) : cloneJSON(BASE_CHECK_MAP)), [agents]);
  const tauMap: Record<string, number> = useMemo(() => {
    const m: Record<string, number> = {};
    for (const [id, a] of Object.entries(agents ?? {})) m[id] = a.tau;
    for (const id of CHECK_IDS) if (!(id in m)) m[id] = BASE_CHECK_MAP[id]?.tau ?? 0.6;
    return m;
  }, [agents]);
  const agentName = (id: string): string =>
    agents?.[id]?.name ?? (id === "_semantic" ? "Semantic triggers" : BASE_CHECK_MAP[id]?.name ?? id);
  const configuredAgentIds = (): string[] => {
    const ids = Object.keys(agents ?? {});
    if (!ids.includes("stateofplay") && Object.values(agents ?? {}).some((agent) => agent.enabled && agent.dest?.stateOfPlay))
      ids.push("stateofplay");
    return ids;
  };

  const evalAttached = !!selEval;
  const autoResolve = !!(selEval && selEval.memoTimeline);

  const initMemos = (): MemosMap => {
    const m: MemosMap = {};
    mattersInScope.forEach((w) => {
      m[w.id] = { phase: w.phase || "Setup", fields: {} };
    });
    return m;
  };

  const rs0 = initialSnapshot;
  const [runState, setRunState] = useState<"idle" | "running" | "paused" | "done">(
    rs0 ? (rs0.runState === "running" ? "paused" : rs0.runState) : "idle",
  );
  const [cursor, setCursor] = useState<number>(rs0 ? rs0.cursor : 0);
  const [processed, setProcessed] = useState<ProcessedEntry[]>(rs0 ? rs0.processed : []);
  const [activeWs, setActiveWs] = useState<string>(
    rs0 ? rs0.activeWs || (mattersInScope[0]?.id || "unassoc") : (mattersInScope[0]?.id || "unassoc"),
  );
  const [pane, setPane] = useState<string>("spine");
  const [expandedCheck, setExpandedCheck] = useState<Record<string, boolean>>({});
  const [showChecks, setShowChecks] = useState<Record<string, boolean>>({});
  const [feedOpen, setFeedOpen] = useState<number | null>(null);
  const [filter, setFilter] = useState<string>("all");

  /* Replay-path approvals (pre-baked corpora keep their seeded queue). Live
     runs queue typed Lawyer Review Tray items instead. */
  const [approvals, setApprovals] = useState<Approval[]>(rs0 ? rs0.approvals : []);
  const [tray, setTray] = useState<TrayItem[]>([]);
  const [memos, setMemos] = useState<MemosMap>(() => (rs0 ? rs0.memos : initMemos()));
  const [staleFields, setStaleFields] = useState<Record<string, Set<string>>>({});
  const [actionsLog, setActionsLog] = useState<ActionLog[]>(rs0 ? rs0.actionsLog : []);
  const [cost, setCost] = useState<CostState>(rs0 ? rs0.cost : { total: 0, perCheck: {}, perEmail: [] });
  const [runWarnings, setRunWarnings] = useState<string[]>([]);
  const [runDegraded, setRunDegraded] = useState(false);
  const [saDispatches, setSaDispatches] = useState<RunPanelData["saDispatches"]>([]);
  const [forceLive, setForceLive] = useState<boolean>(rs0 ? !!rs0.forceLive : false);
  /* Forecast comparison (fourth column). Hidden by default; enabled by upload. */
  const matterId = corpus.matterId || null;
  const [forecasts, setForecasts] = useState<ForecastRunSummary[]>([]);
  const [selForecastId, setSelForecastId] = useState<string | null>(null);
  const [showForecast, setShowForecast] = useState<boolean>(false);
  const [forecastComparison, setForecastComparison] = useState<ForecastComparisonDoc | null>(null);
  const [forecastComputing, setForecastComputing] = useState<boolean>(false);
  const [forecastUploadOpen, setForecastUploadOpen] = useState<boolean>(false);
  const [liveProgress, setLiveProgress] = useState<LiveProgress | null>(null);
  const [progressOpen, setProgressOpen] = useState<boolean>(() => !isMobile);
  const [runError, setRunError] = useState<string | null>(null);
  const liveStateRef = useRef<LiveOrchestratorState | null>(rs0 ? (rs0.liveState as LiveOrchestratorState) ?? null : null);
  /* State BEFORE the last processed email — Recompute reruns that email
     against it after a lawyer resolution changes the ground it stood on. */
  const livePrevStateRef = useRef<LiveOrchestratorState | null>(null);
  const forecastEpochRef = useRef<number>(0);
  const abortRef = useRef<AbortController | null>(null);
  const stoppingRef = useRef<boolean>(false);
  const didMountRef = useRef<boolean>(false);
  /* Highest email index already processed — guards against double-processing
   * when the run loop is paused mid-await and then stepped/resumed. */
  const processedIdxRef = useRef<number>(rs0 ? rs0.cursor - 1 : -1);

  /* Before the first email of a run the display follows the live selection; from
     the first processed email onwards the snapshot governs, so a completed run
     keeps the version it actually ran with. */
  useEffect(() => {
    if (cursor === 0 && runState === "idle") setExecPin(pinned);
  }, [pinned.bundleId, pinned.versionId, pinned.label, cursor, runState, setExecPin]);

  /* Reload this run scope's tray items (resume after reload). */
  useEffect(() => {
    void listTray({ runScope }).then(setTray);
  }, [runScope]);

  const pendingCount =
    tray.filter((t) => t.status === "pending").length +
    approvals.filter((a) => a.status === "pending").length;
  const speedMs = SPEEDS[speedIdx][1];

  useEffect(() => {
    onActiveWsChange?.(activeWs);
  }, [activeWs, onActiveWsChange]);

  /* ---- forecast comparison wiring (unchanged) ---- */
  useEffect(() => {
    if (!matterId) return;
    void listForecasts(matterId).then((fs) => {
      setForecasts(fs);
      setSelForecastId((cur) => cur || (fs[0] ? fs[0].id : null));
    });
  }, [matterId]);

  useEffect(() => {
    if (!selForecastId || processed.length === 0) {
      setForecastComparison(null);
      return;
    }
    void getComparison(selForecastId, workspace.id).then((c) => {
      if (c) setForecastComparison(c);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selForecastId, workspace.id]);

  /* Replay approvals + tray items in the legacy shape the eval scorer and the
     forecast extractor read. */
  const approvalsForEval = useMemo<Approval[]>(() => {
    const fromTray: Approval[] = tray.map((t) => ({
      key: t.id,
      wsId: t.wsId,
      src: t.agentId,
      type: AGENT_EVAL_TYPE[t.agentId] || t.kind,
      label: t.title,
      detail: t.detail,
      severity: t.gate ? "high" : "medium",
      material: t.gate,
      options: t.options ? { field: t.holdField || "", options: t.options, correct: t.options[1] ?? t.options[0] } : null,
      source: null,
      status: t.status === "pending" ? "pending" : t.status === "resolved" ? "approved" : "rejected",
      emailId: Number(t.emailLocalId) || 0,
    }));
    return [...approvals, ...fromTray];
  }, [approvals, tray]);

  async function recomputeForecast(): Promise<void> {
    if (!selForecastId || !processed.length) return;
    const epoch = forecastEpochRef.current;
    setForecastComputing(true);
    try {
      const actuals = extractActualSnapshots({
        processed, approvals: approvalsForEval, mattersInScope, wsNameMap, thresholds: tauMap, CM,
      });
      const c = await compareForecast(selForecastId, workspace.id, actuals);
      if (forecastEpochRef.current !== epoch) return;
      if (c) setForecastComparison(c);
    } finally {
      if (forecastEpochRef.current === epoch) setForecastComputing(false);
    }
  }

  useEffect(() => {
    if (!showForecast || !selForecastId || !processed.length) return;
    debounced("forecast:" + workspace.id, () => void recomputeForecast(), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processed.length, showForecast, selForecastId]);

  function onForecastUploaded(forecastRunId: string): void {
    setForecastUploadOpen(false);
    if (matterId) {
      void listForecasts(matterId).then((fs) => setForecasts(fs));
    }
    setSelForecastId(forecastRunId);
    setShowForecast(true);
  }

  function onForecastDelete(id: string): void {
    if (!window.confirm("Delete this forecast run and its comparisons?")) return;
    void deleteForecast(id).then(() => {
      setForecasts((fs) => {
        const next = fs.filter((f) => f.id !== id);
        setSelForecastId(next[0] ? next[0].id : null);
        if (!next.length) setShowForecast(false);
        return next;
      });
      setForecastComparison(null);
    });
  }

  function applyMemo(wsId: string, k: string, v: string, source?: string): void {
    setMemos((mm) => {
      const ws = mm[wsId] || { phase: "Setup", fields: {} };
      const prev = ws.fields[k];
      const hist = prev ? [...(prev.history || []), { value: prev.value, source: prev.source }] : [];
      return {
        ...mm,
        [wsId]: {
          ...ws,
          fields: { ...ws.fields, [k]: { value: v, source, history: hist } },
        },
      };
    });
  }

  /* ---- replay path: pre-baked corpora (unchanged semantics) ---- */
  function entryFor(i: number): EntryResult {
    const email = emails[i];
    const R0: SimReplay = (email.r as SimReplay) || synthResult(email);
    const eff = (R0.catalogs || []).filter(
      (c) => c[0] === "unassoc" || mattersInScope.some((m) => m.id === c[0]),
    );
    const cats: Array<[string, number]> = eff.length
      ? eff
      : [["unassoc", (R0.catalogs && R0.catalogs[0]) ? R0.catalogs[0][1] : 0.3]];
    const R: SimReplay = { ...R0, catalogs: cats };
    const wsId = cats[0][0];
    const checks: SimCheck[] = R.checks.filter((c) => CM[c.id]);
    const usage: CostEntryDelta[] = checks.map((ch) => ({
      id: ch.id,
      ...meterCheck(CM[ch.id], email),
    }));
    const emailCost = usage.reduce((s, u) => s + u.cost, 0);
    const emailTok = usage.reduce((s, u) => s + u.inTok + u.outTok, 0);
    const apprsRaw: any[] = wsId === "unassoc" ? [] : (R.approvals || []);
    const apprs: Approval[] = apprsRaw
      .filter((a) => {
        if (!CM[a[0]]) return false;
        const cc = R.checks.find((c) => c.id === a[0]);
        const tau = tauMap[a[0]] ?? 0.6;
        if (cc && cc.conf < tau) return false;
        return true;
      })
      .map(
        (a, k): Approval => ({
          key: email.id + "-" + k,
          wsId,
          src: a[0],
          type: a[1],
          label: a[2],
          detail: a[3],
          material: a[4],
          options: a[1] === "conflict" ? a[5] : null,
          source: a[1] === "deal-memo" ? a[5] : null,
          status: "pending",
          emailId: email.id,
        }),
      );
    const acts: ActionLog[] = (R.actions || []).map((a) => ({
      agent: LEGACY_AGENT_ALIAS[a[0]] || a[0],
      text: a[1],
      emailId: email.id,
    }));
    return { entry: { email, checks, R }, apprs, acts, emailCost, emailTok, usage };
  }

  /* ---- live path: the generic executor runs the pinned bundle version
     server-side (engine v2). No prompt/model/τ payload leaves the client —
     what the registry shows is what runs. ---- */
  const shouldLive = (email: SimEmail): boolean => forceLive || !email.r;
  async function entryForLive(i: number): Promise<EntryResult> {
    const email = emails[i];
    let serverId = email.serverId;
    if (!serverId && onEnsureSynced) {
      const ids = await onEnsureSynced();
      serverId = ids?.[email.id];
    }
    if (!serverId)
      throw new Error(
        "email is not synced to the backend yet — check connectivity, then reopen the workspace",
      );
    if (!agents)
      throw new Error(pinError || "the pinned bundle version has not loaded yet");
    /* Use the run-start snapshot, not the live prop — the dropdown may have changed
       mid-run.  runPinnedRef is set in start() / step() before any email is processed. */
    const execPinned = runPinnedRef.current;
    const fresh: LiveOrchestratorState = {
      memos: cloneJSON(memos),
      documentRegister: {},
      participantMap: {},
      threadHistory: [],
      trackers: {},
    };
    const baseState: LiveOrchestratorState =
      i === 0 || !liveStateRef.current ? fresh : liveStateRef.current;
    const fan = Object.keys(agents).filter((id) => agents[id].enabled);
    setLiveProgress({ emailId: email.id, running: fan, done: [] });
    const controller = new AbortController();
    abortRef.current = controller;
    let res: ExecResult;
    try {
      res = await streamExecute(
        serverId,
        {
          bundleVersionId: execPinned.versionId,
          workstreams: mattersInScope.map((w) => ({
            id: w.id, name: w.name, code: w.code, entities: w.entities || [],
          })),
          state: baseState,
          localId: email.id,
          runScope,
        },
        (c) =>
          setLiveProgress((p) =>
            p && p.emailId === email.id && !p.done.includes(c.id)
              ? { ...p, done: [...p.done, c.id] }
              : p,
          ),
        controller.signal,
      );
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
    livePrevStateRef.current = baseState;
    liveStateRef.current = res.updatedState;
    const history = res.updatedState.threadHistory || [];
    const last: any = history[history.length - 1] || {};
    const routes = res.routes.length ? res.routes : [{ workstreamId: "unassoc", confidence: res.routing.confidence ?? 0.3 }];
    /* Skipped agents are kept (greyed in the Spine Viewer with their real
       skip reason); parked ones carry their reason in `out`. */
    const checks: SimCheck[] = res.checks.map((c) => ({
      id: c.id,
      conf: c.conf,
      out: c.out,
      ok: c.ok,
      parked: c.parked,
      live: true,
      skipped: c.skipped || undefined,
      reason: c.reason,
      result: c.result || undefined,
    }));
    const usage: CostEntryDelta[] = res.checks
      .filter((c) => !c.skipped)
      .map((c) => ({
        id: c.id,
        inTok: c.usage.input_tokens || 0,
        outTok: c.usage.output_tokens || 0,
        cost: c.usage.cost || 0,
        model: c.model || "",
        calls: 1,
        latency: c.latencyMs || 0,
      }));
    const emailCost = res.usage.cost;
    const emailTok = res.usage.input_tokens + res.usage.output_tokens;
    /* Fold the run's tray outcome into the run-scope queue: new items appended,
       superseded ones flipped. */
    setTray((cur) => {
      const byId = new Map(cur.map((t) => [t.id, t]));
      for (const t of res.tray) byId.set(t.id, t);
      for (const sid of res.superseded) {
        const t = byId.get(sid);
        if (t) byId.set(sid, { ...t, status: "superseded" });
      }
      return [...byId.values()];
    });
    setRunWarnings(res.warnings);
    setRunDegraded(!!res.degraded);
    if (res.saDispatches.length) setSaDispatches((s) => [...s, ...res.saDispatches]);
    /* Tags derived from the real results so the spine filters work in Live. */
    const tags: Array<[string]> = [];
    if ((res.facts || []).length) tags.push(["memo"]);
    if ((email.attachments || []).length) tags.push(["doc"]);
    if (res.tray.some((t) => t.agentId === "conflict" || t.agentId === "anomaly")) tags.push(["anom"]);
    if (routes.length > 1) tags.push(["multi"]);
    if (checks.some((c) => c.id === "privilege" && !c.skipped && c.conf >= (tauMap.privilege ?? 0.7)))
      tags.push(["priv"]);
    if (routes[0].workstreamId === "unassoc") tags.push(["noise"]);
    if (!tags.length) tags.push(["log"]);
    const R: SimReplay = {
      catalogs: routes.map((r) => [r.workstreamId, r.confidence]),
      delta: last.delta || last.deltaPreview || "",
      facts: (res.facts || []) as any,
      tags,
      checks,
      approvals: [],
      actions: [],
      live: true,
    };
    /* Actions feed: every fired non-role agent with output. */
    const acts: ActionLog[] = res.checks
      .filter((c) => !c.skipped && !c.id.startsWith("_") && (c.out || c.result))
      .filter((c) => {
        const role = agents[c.id]?.role;
        return role !== "cleaner" && role !== "router" && c.id !== "participant";
      })
      .map((c) => ({ agent: c.id, text: c.out, emailId: email.id, result: c.result || undefined }));
    setLiveProgress(null);
    return {
      entry: { email, checks, R },
      apprs: [],
      acts,
      emailCost,
      emailTok,
      usage,
      liveMemos: (res.updatedState.memos || {}) as MemosMap,
    };
  }

  /* Processes email i and folds the result into the run state. Returns true
     only when an entry was actually appended — callers must NOT advance the
     cursor otherwise, or the feed silently marches past emails that were
     never processed. */
  async function processOne(i: number, isCancelled?: () => boolean): Promise<boolean> {
    if (i >= total || i <= processedIdxRef.current) return false;
    processedIdxRef.current = i;
    const live = shouldLive(emails[i]);
    let r: EntryResult;
    try {
      r = live ? await entryForLive(i) : entryFor(i);
    } catch (e) {
      processedIdxRef.current = Math.min(processedIdxRef.current, i - 1);
      setLiveProgress(null);
      throw e;
    }
    if (isCancelled && isCancelled()) {
      processedIdxRef.current = Math.min(processedIdxRef.current, i - 1);
      setLiveProgress(null);
      return false;
    }
    const { entry, apprs, acts, emailCost, emailTok, usage, liveMemos } = r;
    setProcessed((p) => [...p, entry]);
    setCost((c) => {
      const perCheck = { ...c.perCheck };
      usage.forEach((u) => {
        const cur: CostUsageEntry = perCheck[u.id] || {
          inTok: 0, outTok: 0, cost: 0, model: u.model, calls: 0,
        };
        perCheck[u.id] = {
          inTok: cur.inTok + u.inTok,
          outTok: cur.outTok + u.outTok,
          cost: cur.cost + u.cost,
          model: u.model,
          calls: (cur.calls || 0) + (u.calls || 0),
          latency: u.latency || cur.latency,
        };
      });
      return {
        total: c.total + emailCost,
        perCheck,
        perEmail: [
          ...c.perEmail,
          { id: entry.email.id, subject: entry.email.subject, cost: emailCost, tok: emailTok },
        ],
      };
    });
    const apprsOut: Approval[] =
      autoResolve && !live
        ? apprs.map((a) => ({
            ...a,
            status: "approved",
            chosen: a.options ? a.options.correct : a.chosen,
          }))
        : apprs;
    if (apprsOut.length) setApprovals((p) => [...p, ...apprsOut]);
    if (acts.length) setActionsLog((p) => [...p, ...acts]);
    if (live) {
      if (liveMemos) setMemos((mm) => ({ ...mm, ...liveMemos }));
    } else if (autoResolve && selEval) {
      ((selEval.memoTimeline && selEval.memoTimeline[entry.email.id]) || []).forEach((w) =>
        applyMemo(w.ws, w.key, w.value, w.source),
      );
      const ph = selEval.phaseTimeline && selEval.phaseTimeline[entry.email.id];
      if (ph)
        setMemos((mm) => ({
          ...mm,
          [ph.ws]: { ...(mm[ph.ws] || { fields: {} }), phase: ph.name },
        }));
    }
    const top = entry.R.catalogs[0][0];
    if (top !== "unassoc") setActiveWs(top);
    return true;
  }

  useEffect(() => {
    if (runState !== "running") return;
    if (cursor >= total) {
      /* An empty corpus must never "complete" — park instead. */
      if (total === 0) {
        setRunState("idle");
        return;
      }
      completeRun(processed, cost, approvalsForEval);
      setRunState("done");
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const did = await processOne(cursor, () => cancelled || stoppingRef.current);
        if (cancelled || stoppingRef.current) return;
        if (did) setCursor((c) => c + 1);
        else setRunState("paused");
      } catch (e: any) {
        if (!cancelled && !isCancelError(e)) {
          setRunError(`Email #${emails[cursor]?.id ?? cursor + 1} failed: ${e?.message || e}`);
          setRunState("paused");
        }
      }
    }, speedMs);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runState, cursor, speedMs]);

  function start(): void {
    /* Snapshot the current selection at run start. Changing the dropdown after
       this point only affects the NEXT run (fresh reset + start), not the
       emails still to be processed in this run. */
    if (cursor === 0 || cursor >= total) setExecPin(pinned);
    if (cursor >= total) {
      liveStateRef.current = null;
      reset();
    }
    setRunError(null);
    setRunState("running");
  }
  async function step(): Promise<void> {
    /* One email in flight → a second Step must be a no-op. */
    if (cursor >= total || liveProgress !== null) return;
    /* Snapshot on the first step of a fresh run (same semantics as start()). */
    if (cursor === 0) setExecPin(pinned);
    setRunState("paused");
    setRunError(null);
    try {
      const did = await processOne(cursor);
      if (did) setCursor((c) => c + 1);
    } catch (e: any) {
      if (!isCancelError(e))
        setRunError(`Email #${emails[cursor]?.id ?? cursor + 1} failed: ${e?.message || e}`);
    }
  }
  /* Kill switch: abort the in-flight live run and park the simulation. The
     current email is discarded and re-runs on resume. */
  function stopRun(): void {
    stoppingRef.current = true;
    abortRef.current?.abort();
    abortRef.current = null;
    setRunState("paused");
    setLiveProgress(null);
    setTimeout(() => {
      stoppingRef.current = false;
    }, 0);
  }
  function runToEnd(): void {
    if (forceLive || emails.slice(cursor).some((e) => !e.r)) {
      setRunState("running");
      return;
    }
    const proc = [...processed];
    let apprs = [...approvals];
    const acts = [...actionsLog];
    const c: CostState = {
      total: cost.total,
      perCheck: { ...cost.perCheck },
      perEmail: [...cost.perEmail],
    };
    for (let i = cursor; i < total; i++) {
      const r = entryFor(i);
      proc.push(r.entry);
      apprs.push(...r.apprs);
      acts.push(...r.acts);
      c.total += r.emailCost;
      c.perEmail.push({
        id: r.entry.email.id,
        subject: r.entry.email.subject,
        cost: r.emailCost,
        tok: r.emailTok,
      });
      r.usage.forEach((u) => {
        const cur: CostUsageEntry = c.perCheck[u.id] || {
          inTok: 0, outTok: 0, cost: 0, model: u.model, calls: 0,
        };
        c.perCheck[u.id] = {
          inTok: cur.inTok + u.inTok,
          outTok: cur.outTok + u.outTok,
          cost: cur.cost + u.cost,
          model: u.model,
          calls: (cur.calls || 0) + (u.calls || 0),
          latency: u.latency || cur.latency,
        };
      });
    }
    if (autoResolve) {
      apprs = apprs.map((a) => ({
        ...a,
        status: "approved",
        chosen: a.options ? a.options.correct : a.chosen,
      }));
    }
    const memosOut: MemosMap = autoResolve && selEval
      ? buildMemosFromEval(selEval, initMemos(), proc.map((p) => p.email))
      : memos;
    setProcessed(proc);
    setApprovals(apprs);
    setActionsLog(acts);
    setCost(c);
    setMemos(memosOut);
    setCursor(total);
    processedIdxRef.current = total - 1;
    setRunState("done");
    completeRun(proc, c, apprs, memosOut, acts);
  }
  function completeRun(
    proc: ProcessedEntry[],
    c: CostState,
    appr: Approval[],
    memosArg?: MemosMap,
    actsArg?: ActionLog[],
  ): void {
    const finalMemos = memosArg || memos;
    const ev = computeEval(proc, appr, tauMap, new Set(), selEval ? selEval.expected : null);
    const memoMatch = computeMemoMatch(finalMemos, selEval);
    ev.memoMatch = memoMatch;
    ev.perCheck = computePerCheck(proc, selEval ? selEval.expected : null);
    ev.trackerMatch = computeTrackerMatch(
      selEval?.finalTrackers,
      liveStateRef.current?.trackers,
    );
    ev.alignment = computeAlignment(ev.perCheck, ev.trackerMatch);
    const matters = mattersInScope.map((w) => ({
      id: w.id,
      name: w.name,
      phase: w.phase || "Setup",
    }));
    const run: CompletedRun = {
      id: workspace.id || "run-" + Date.now().toString().slice(-5),
      when: new Date().toLocaleString(),
      config: {
        corpus: corpus.name,
        emails: proc.length,
        checkVersion: runPinnedRef.current.label,
        matters: matters.map((m) => m.name),
        evalName: selEval ? selEval.name : "none",
        togglesOff: [],
        agentsOff: 0,
      },
      eval: ev.noEval
        ? null
        : {
            catPct: ev.catPct,
            detPct: ev.detPct,
            catOK: ev.catOK,
            catN: ev.catN,
            detOK: ev.detOK,
            detN: ev.detN,
            memoPct: memoMatch ? memoMatch.pct : null,
            memoOK: memoMatch ? memoMatch.ok : null,
            memoN: memoMatch ? memoMatch.n : null,
          },
      usage: {
        total: c.total,
        perCheck: c.perCheck,
        tok: Object.values(c.perCheck).reduce(
          (s, u) => s + ((u.inTok || 0) + (u.outTok || 0)),
          0,
        ),
      },
      approvalsCount: appr.length,
      replay: {
        processed: proc,
        approvals: appr,
        memos: finalMemos,
        actionsLog: actsArg || actionsLog,
        cost: c,
        thresholds: { ...tauMap },
        toggledOff: [],
        activeAgents: configuredAgentIds(),
        trackerKeys: Object.values(agents ?? {}).flatMap((agent) => [
          ...(agent.dest?.tracker ? [agent.dest.tracker.key] : []),
          ...(agent.dest?.trackers || []).map((tracker) => tracker.key),
        ]),
        trackers: (liveStateRef.current?.trackers ?? {}) as Record<string, Record<string, Array<Record<string, unknown>>>>,
        CM: cloneJSON(CM),
        checkList: Object.keys(CM).map((id) => ({ id, ...CM[id] })),
        wsNameMap: { ...wsNameMap },
        matters,
        documentRegister: accumulateDocumentRegister(proc),
        evalResult: ev,
        evalName: selEval ? selEval.name : "",
        checkVersion: runPinnedRef.current.label,
        corpus: corpus.name,
      },
    };
    onComplete(workspace.id, run);
    cancelDebounced("runState:" + workspace.id);
    void deleteRunState(workspace.id);
  }
  function reset(): void {
    stoppingRef.current = true;
    abortRef.current?.abort();
    abortRef.current = null;
    setTimeout(() => {
      stoppingRef.current = false;
    }, 0);
    /* Reset snapshot to current selection so a fresh run picks up any version change. */
    setExecPin(pinned);
    setRunState("idle");
    setRunError(null);
    setCursor(0);
    processedIdxRef.current = -1;
    setProcessed([]);
    setApprovals([]);
    setMemos(initMemos());
    setStaleFields({});
    setActionsLog([]);
    setCost({ total: 0, perCheck: {}, perEmail: [] });
    setExpandedCheck({});
    setShowChecks({});
    setFeedOpen(null);
    setRunWarnings([]);
    setRunDegraded(false);
    setSaDispatches([]);
    liveStateRef.current = null;
    livePrevStateRef.current = null;
    setLiveProgress(null);
    /* A fresh run starts with an empty tray: clear this run scope's items. */
    setTray([]);
    void clearTrayScope(runScope);
    forecastEpochRef.current += 1;
    cancelDebounced("forecast:" + workspace.id);
    setForecastComparison(null);
    setForecastComputing(false);
    if (selForecastId) void deleteComparison(selForecastId, workspace.id);
  }

  /* ---- tray resolution (live) + replay-approval decisions ---- */
  async function onResolveTray(
    item: TrayItem,
    action: "approve" | "reject" | "choose" | "answer" | "acknowledge",
    extra?: { choice?: string; text?: string },
  ): Promise<void> {
    const r = await resolveTrayItem(item.id, action, extra ?? {});
    if (!r) return;
    setTray((cur) => cur.map((t) => (t.id === item.id ? r.item : t)));
    /* Merge the write-back into the carried live state + visible memo, so
       subsequent emails process against the decided value. */
    if (r.writeBack) {
      const wses = item.matters.length ? item.matters : [item.wsId];
      for (const ws of wses) {
        applyMemo(ws, r.writeBack.field, r.writeBack.value, "Resolved by lawyer");
        const st = liveStateRef.current;
        if (st) {
          st.memos = st.memos || {};
          const m = (st.memos[ws] = st.memos[ws] || { phase: null, fields: {} });
          const prev = m.fields[r.writeBack.field];
          m.fields[r.writeBack.field] = {
            value: r.writeBack.value,
            source: "Resolved by lawyer",
            history: prev ? [...(prev.history || []), { value: prev.value, source: prev.source }] : [],
          };
        }
      }
      /* Phase gates advance the phase on approval. */
      if (item.holdField === "phase" && action === "approve") {
        const m = item.title.match(/→\s*(.+)$/);
        const next = m?.[1]?.trim() || r.writeBack.value;
        for (const ws of wses)
          setMemos((mm) => ({ ...mm, [ws]: { ...(mm[ws] || { fields: {} }), phase: next } }));
      }
      if (r.staleFields.length) {
        setStaleFields((s) => {
          const out = { ...s };
          for (const ws of wses) out[ws] = new Set([...(out[ws] || []), ...r.staleFields]);
          return out;
        });
      }
    }
  }

  /* Recompute: re-run the last processed email against the state it originally
     saw — now including the lawyer's resolution — and replace its entry.
     Manual by design (no auto-recompute in v1). */
  const [recomputing, setRecomputing] = useState(false);
  async function recomputeLast(): Promise<void> {
    const i = processedIdxRef.current;
    if (i < 0 || recomputing || liveProgress) return;
    const email = emails[i];
    if (!email?.serverId || !agents) return;
    setRecomputing(true);
    try {
      const base = livePrevStateRef.current ?? { memos: cloneJSON(memos) };
      /* Fold resolved values into the base so the rerun sees them. */
      const st = liveStateRef.current;
      if (st?.memos && base.memos) {
        for (const [ws, m] of Object.entries(st.memos)) {
          const bm = (base.memos[ws] = base.memos[ws] || { phase: null, fields: {} });
          for (const [k, v] of Object.entries(m.fields)) {
            if ((v as { source?: string }).source === "Resolved by lawyer") bm.fields[k] = v;
          }
        }
      }
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await streamExecute(
        email.serverId,
        {
          /* Recompute uses the same snapshot the original run used. */
          bundleVersionId: runPinnedRef.current.versionId,
          workstreams: mattersInScope.map((w) => ({ id: w.id, name: w.name, code: w.code, entities: w.entities || [] })),
          state: base as LiveOrchestratorState,
          localId: email.id,
          runScope,
        },
        () => {},
        controller.signal,
      );
      liveStateRef.current = res.updatedState;
      if (res.updatedState.memos) setMemos((mm) => ({ ...mm, ...(res.updatedState.memos as MemosMap) }));
      setStaleFields({});
      setTray((cur) => {
        const byId = new Map(cur.map((t) => [t.id, t]));
        for (const t of res.tray) byId.set(t.id, t);
        return [...byId.values()];
      });
      const routes = res.routes.length ? res.routes : [{ workstreamId: "unassoc", confidence: res.routing.confidence ?? 0.3 }];
      setProcessed((p) =>
        p.map((entry, k) =>
          k === i
            ? {
                ...entry,
                checks: res.checks.map((c) => ({
                  id: c.id, conf: c.conf, out: c.out, live: true,
                  ok: c.ok, parked: c.parked,
                  skipped: c.skipped || undefined, reason: c.reason, result: c.result || undefined,
                })),
                R: { ...entry.R, catalogs: routes.map((r) => [r.workstreamId, r.confidence]), facts: res.facts as any },
              }
            : entry,
        ),
      );
    } catch (e) {
      if (!isCancelError(e)) setRunError(`Recompute failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setRecomputing(false);
      abortRef.current = null;
    }
  }

  function decideReplay(key: string, ok: boolean, chosen?: string): void {
    const a = approvals.find((x) => x.key === key);
    setApprovals((list) =>
      list.map((x) => (x.key === key ? { ...x, status: ok ? "approved" : "rejected", chosen } : x)),
    );
    if (!a || !ok) return;
    if (a.type === "deal-memo") {
      const m = a.label.match(/^(.+?)\s*(?:=|→)\s*(.+)$/);
      if (m) {
        const k = m[1].trim();
        let v = m[2].trim();
        const wm = v.match(/^(.+?)\s*\(was/);
        if (wm) v = wm[1].trim();
        applyMemo(a.wsId, k, v, a.source);
      }
    }
    if (a.type === "conflict") {
      const f = a.options ? a.options.field : null;
      const v = chosen || (a.options ? a.options.correct : null);
      if (f && v) applyMemo(a.wsId, f, v, "Resolved by lawyer");
    }
    if (a.type === "phase") {
      const m = a.label.match(/→\s*(.+)$/);
      if (m)
        setMemos((mm) => ({
          ...mm,
          [a.wsId]: { ...(mm[a.wsId] || { fields: {} }), phase: m[1].trim() },
        }));
    }
  }

  /* Replay approvals rendered as tray-shaped items so the panel is the one
     queue for both paths. */
  const replayTrayItems = useMemo<TrayItem[]>(
    () =>
      approvals.map((a) => ({
        id: a.key,
        wsId: a.wsId,
        matters: [a.wsId],
        agentId: a.src || a.type,
        kind: a.type === "conflict" ? "choice" : "decision",
        gate: a.type === "conflict" || a.type === "phase",
        holdField: a.options?.field ?? (a.type === "phase" ? "phase" : null),
        title: a.label,
        detail: a.detail || "",
        options: a.options?.options ?? null,
        status: a.status === "pending" ? "pending" : a.status === "approved" ? "resolved" : "expired",
        resolution: null,
        recurrence: [],
        justification: null,
        emailLocalId: String(a.emailId),
        createdAt: "",
        resolvedAt: null,
      })),
    [approvals],
  );

  /* ---- derived per-render ---- */
  const wsEntries = useMemo<Record<string, SpineCardEntry[]>>(() => {
    const map: Record<string, SpineCardEntry[]> = { unassoc: [] };
    mattersInScope.forEach((w) => {
      map[w.id] = [];
    });
    processed.forEach((p) => {
      p.R.catalogs.forEach((c) => {
        if (map[c[0]]) map[c[0]].push({ p, conf: c[1] });
      });
    });
    return map;
  }, [processed, mattersInScope]);
  const entityNames = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    mattersInScope.forEach((w) =>
      (w.entities || []).forEach((e) => {
        m[e.id] = e.name;
      }),
    );
    return m;
  }, [mattersInScope]);
  const addressRoster = useMemo(() => buildAddressRoster(mattersInScope), [mattersInScope]);
  const docRegister = useMemo(() => {
    const live = liveStateRef.current?.documentRegister;
    if (live && Object.keys(live).length) return live as Record<string, any[]>;
    return accumulateDocumentRegister(processed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processed]);
  const evalResult = useMemo(() => {
    const ev = computeEval(processed, approvalsForEval, tauMap, new Set(), selEval ? selEval.expected : null);
    ev.memoMatch = computeMemoMatch(memos, selEval);
    ev.perCheck = computePerCheck(processed, selEval ? selEval.expected : null);
    ev.trackerMatch = computeTrackerMatch(
      selEval?.finalTrackers,
      liveStateRef.current?.trackers,
    );
    ev.alignment = computeAlignment(ev.perCheck, ev.trackerMatch);
    return ev;
  }, [processed, approvalsForEval, tauMap, selEval, memos]);

  /* In-progress run snapshot (persisted; restored on reopen). */
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const key = "runState:" + workspace.id;
    if (runState === "done") {
      cancelDebounced(key);
      void deleteRunState(workspace.id);
      return;
    }
    const snap: RunStateSnapshot = {
      runState,
      cursor,
      processed,
      approvals,
      memos,
      actionsLog,
      cost,
      thresholds: tauMap,
      toggledOff: [],
      activeAgents: configuredAgentIds(),
      activeWs,
      forceLive,
      liveState: liveStateRef.current,
    };
    debounced(key, () => void saveRunState(workspace.id, snap));
  }, [
    workspace.id, runState, cursor, processed, approvals, memos, actionsLog,
    cost, activeWs, forceLive, agents, tauMap,
  ]);

  const spineMatters: SpineMatter[] = mattersInScope.map((w) => ({ id: w.id, name: w.name }));

  const onViewSpine = (top: string): void => {
    setActiveWs(top);
    if (isCompact) setPane("spine");
  };
  const routedWs = (emailId: number): string => {
    const p = processed.find((x) => x.email.id === emailId);
    return (p && p.R.catalogs[0] && p.R.catalogs[0][0]) || "unassoc";
  };

  /* The run-result panel's accumulated view of this run. */
  const latestChecks: ExecCheckRecord[] = useMemo(() => {
    const last = processed[processed.length - 1];
    if (!last) return [];
    return last.checks.map((c) => ({
      id: c.id,
      name: agentName(c.id),
      skipped: !!c.skipped,
      ok: c.ok ?? !c.skipped,
      parked: c.parked ?? (!c.skipped && /\(parked|below τ/.test(c.out || "") ? true : undefined),
      conf: c.conf,
      out: c.out,
      result: (c.result as Record<string, unknown>) || null,
      model: "",
      latencyMs: 0,
      usage: { input_tokens: 0, output_tokens: 0, cost: 0 },
      reason: c.reason,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processed]);
  const panelData: RunPanelData = useMemo(() => {
    const perAgent: RunPanelData["usage"]["perAgent"] = {};
    for (const [id, u] of Object.entries(cost.perCheck))
      perAgent[id] = { cost: u.cost, calls: u.calls || 0, model: u.model };
    const last = processed[processed.length - 1];
    const memosWithStale: RunPanelData["memos"] = {};
    for (const [ws, m] of Object.entries(memos)) {
      memosWithStale[ws] = {
        phase: m.phase ?? null,
        fields: Object.fromEntries(
          Object.entries(m.fields).map(([k, v]) => [
            k,
            { ...v, stale: staleFields[ws]?.has(k) || undefined },
          ]),
        ),
      };
    }
    return {
      routes: last
        ? last.R.catalogs.filter((c) => c[0] !== "unassoc").map((c) => ({ workstreamId: c[0], confidence: c[1] }))
        : [],
      unrouted: !!last && last.R.catalogs[0]?.[0] === "unassoc",
      tray: [...tray, ...replayTrayItems],
      memos: memosWithStale,
      register: docRegister as RunPanelData["register"],
      trackers: (liveStateRef.current?.trackers ?? {}) as RunPanelData["trackers"],
      checks: latestChecks,
      usage: { cost: cost.total, perAgent },
      saDispatches,
      warnings: runWarnings,
      degraded: runDegraded,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processed, tray, replayTrayItems, memos, staleFields, cost, latestChecks, runWarnings, saDispatches, runDegraded]);

  const Feed = (
    <FeedCol
      emails={emails}
      cursor={cursor}
      runState={runState}
      feedOpen={feedOpen}
      setFeedOpen={setFeedOpen}
      routedWs={routedWs}
      onView={onViewSpine}
    />
  );
  const Spine = (
    <SpineCol
      mattersInScope={spineMatters}
      wsName={wsName}
      wsEntries={wsEntries}
      activeWs={activeWs}
      setActiveWs={setActiveWs}
      filter={filter}
      setFilter={setFilter}
      showChecks={showChecks}
      setShowChecks={setShowChecks}
      expandedCheck={expandedCheck}
      setExpandedCheck={setExpandedCheck}
      thresholds={tauMap}
      CM={CM}
      cursor={cursor}
      total={total}
      entityNames={entityNames}
      addressRoster={addressRoster}
    />
  );
  const Right = (
    <div className="scol" style={{ padding: "10px 12px", overflow: "hidden" }}>
      <RunResultPanel
        data={panelData}
        wsName={wsName}
        tauFor={(id) => agents?.[id]?.tau ?? null}
        agentName={agentName}
        onResolve={(item, action, extra) => {
          if (replayTrayItems.some((t) => t.id === item.id))
            decideReplay(item.id, action !== "reject", extra?.choice);
          else void onResolveTray(item, action, extra);
        }}
        onRuling={(item, text) => void recordRuling(item.id, text)}
        onRecompute={() => void recomputeLast()}
        extraTabs={
          evalAttached
            ? [{ id: "eval", label: "Eval", render: () => <EvalPanel ev={evalResult} evalName={selEval ? selEval.name : ""} /> }]
            : []
        }
        emptyHint="Nothing needs the lawyer yet — items appear as agents escalate."
      />
    </div>
  );

  const Controls = (
    <div className="actions">
      <div
        style={{
          display: "flex",
          border: "1px solid var(--bd2)",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        {SPEEDS.map((s, i) => (
          <button
            key={i}
            onClick={() => setSpeedIdx(i)}
            className="mono"
            title={s[0] === "⚡" ? "Instant — skip playback delay" : s[0] + " playback speed"}
            style={{
              padding: "7px 9px",
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              background: speedIdx === i ? "var(--fg)" : "transparent",
              color: speedIdx === i ? "#fff" : "var(--muted)",
            }}
          >
            {s[0] === "⚡" ? <Zap size={13} /> : s[0]}
          </button>
        ))}
      </div>
      <button
        className="icon-btn"
        title={liveProgress ? "Step — waiting for the current email to finish" : "Step"}
        onClick={step}
        disabled={cursor >= total || liveProgress !== null}
      >
        <SkipForward size={17} />
      </button>
      {runState === "running" ? (
        <button className="btn btn-primary btn-sm" onClick={() => setRunState("paused")}>
          <Pause size={14} /> Pause
        </button>
      ) : (
        <button
          className="btn btn-primary btn-sm"
          onClick={start}
          disabled={total === 0 || (cursor >= total && runState === "done")}
        >
          <Play size={14} /> Run
        </button>
      )}
      <button className="icon-btn" title="Run to end" onClick={runToEnd} disabled={cursor >= total}>
        <FastForward size={17} />
      </button>
      <button
        className="icon-btn"
        title="Stop — kill the running agents and cancel the in-flight email"
        onClick={stopRun}
        disabled={runState !== "running" && !liveProgress}
        style={{ color: runState === "running" || liveProgress ? "var(--red)" : undefined }}
      >
        <Square size={15} />
      </button>
      <button className="icon-btn" title="Reset" onClick={reset}>
        <RotateCcw size={16} />
      </button>
      <button
        className="btn btn-sm"
        title={
          forceLive
            ? "Live on — every email runs through the executor against the pinned bundle version"
            : "Force every email through the live executor (otherwise pre-baked results replay)"
        }
        onClick={() => setForceLive((v) => !v)}
        disabled={runState === "running"}
        style={{
          borderColor: forceLive ? "var(--green)" : "var(--bd2)",
          color: forceLive ? "var(--green)" : "var(--muted)",
        }}
      >
        <Zap size={14} /> Live {forceLive ? "on" : "off"}
      </button>
      <button
        className="icon-btn"
        title={
          matterId
            ? "Export Corpus — download emails + attachments as a ZIP for external forecast generation"
            : "Export Corpus — unavailable until this corpus is synced to the backend"
        }
        onClick={() => window.open(corpusExportUrl(matterId!), "_blank")}
        disabled={!matterId}
      >
        <Download size={16} />
      </button>
      <button
        className="icon-btn"
        title={
          matterId
            ? "Upload Forecast — externally-generated prediction JSON"
            : "Upload Forecast — unavailable until this corpus is synced to the backend"
        }
        onClick={() => setForecastUploadOpen(true)}
        disabled={!matterId}
      >
        <Upload size={16} />
      </button>
      {!isCompact && (
        <button
          className="btn btn-sm"
          title={
            showForecast
              ? "Hide the forecast comparison panel"
              : forecasts.length
                ? "Show the forecast comparison panel"
                : "Show the forecast panel — upload a forecast to compare"
          }
          onClick={() => setShowForecast((v) => !v)}
          style={{
            borderColor: showForecast ? "var(--blue)" : "var(--bd2)",
            color: showForecast ? "var(--blue)" : "var(--muted)",
          }}
        >
          <Zap size={14} /> Forecast
        </button>
      )}
      {recomputing && (
        <span className="mono tert" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Loader2 size={12} className="spinrot" /> recomputing…
        </span>
      )}
      {liveProgress && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            marginLeft: 8,
          }}
        >
          <Loader2 size={13} className="spinrot" style={{ color: "var(--green)" }} />
          <span className="mono tert" style={{ fontSize: 11 }}>
            live · email #{liveProgress.emailId} · {liveProgress.done.length}/
            {liveProgress.running.length} agents
          </span>
          <button
            className="icon-btn"
            style={{ padding: 3 }}
            title={progressOpen ? "Hide per-agent progress" : "Show per-agent progress"}
            aria-expanded={progressOpen}
            onClick={() => setProgressOpen((v) => !v)}
          >
            {progressOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {progressOpen && liveProgress.running.map((id) => {
            const done = liveProgress.done.includes(id);
            return (
              <span
                key={id}
                className="pill"
                style={{
                  fontSize: 10,
                  background: "var(--subtle)",
                  color: done ? "var(--green)" : "var(--muted)",
                }}
              >
                {done ? <Check size={10} /> : <Loader2 size={10} className="spinrot" />}{" "}
                {agentName(id)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <main className="page" id="main">
      <div className="pagehd">
        <div className="runhd">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            <ChevronLeft size={14} /> Simulations
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1
              className="h1"
              style={{
                fontSize: 20,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {workspace.name}
            </h1>
            <div className="psub">
              {corpus.name} · {mattersInScope.length} matter
              {mattersInScope.length === 1 ? "" : "s"}
              {selEval ? " · " + selEval.name : " · no eval"} · created{" "}
              {fmtDate(workspace.createdAt)}{" "}
              {/* Pinned bundle version. Swappable mid-run: emails processed
                 from here on run the newly selected version server-side, while
                 already-processed emails keep their results. Locked only while
                 an email is actively in flight. */}
              <span
                className="ttrigger"
                style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 6 }}
                data-tip={
                  runState === "running" || liveProgress !== null
                    ? "Pause the run to change the bundle — an email is currently being processed."
                    : "Agent bundle version — applies to emails processed from here on."
                }
              >
                <select
                  className="sel"
                  style={{ padding: "3px 26px 3px 9px", fontSize: 12, borderRadius: 5 }}
                  aria-label="Agent bundle version"
                  value={selVer}
                  disabled={runState === "running" || liveProgress !== null}
                  onChange={(e) => onChangeCheckVer(e.target.value)}
                >
                  {versionOpts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.short}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          </div>
        </div>
        <div className="actions">{Controls}</div>
      </div>
      {total === 0 && (
        <div
          className="donebar"
          role="alert"
          style={{ color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}
        >
          This corpus has no emails on the backend — the original import may have failed, so
          there is nothing to run. Re-import the corpus in the Library, then reopen this
          workspace.
        </div>
      )}
      {pinError && (
        <div className="donebar" role="alert" style={{ color: "var(--red)" }}>
          {pinError}
        </div>
      )}
      {runError && (
        <div
          className="donebar"
          role="alert"
          style={{ color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}
        >
          Run paused — {runError}
          <button className="lnk" onClick={() => setRunError(null)}>Dismiss</button>
        </div>
      )}
      {runState === "done" && (
        <div className="donebar">
          <Check size={14} color={cvar("--green")} /> Run complete — this workspace is now saved
          under Completed. <button className="lnk" onClick={onBack}>Back to Simulations →</button>
        </div>
      )}
      {isCompact ? (
        <>
          <div className="tabbar">
            <button className={pane === "feed" ? "on" : ""} onClick={() => setPane("feed")}>
              <Inbox size={15} /> Feed
            </button>
            <button className={pane === "spine" ? "on" : ""} onClick={() => setPane("spine")}>
              <GitBranch size={15} /> Spine
            </button>
            <button className={pane === "panel" ? "on" : ""} onClick={() => setPane("panel")}>
              <Activity size={15} /> Panel
              {pendingCount > 0 && (
                <span className="cbadge" style={{ color: "var(--red)" }}>
                  {pendingCount}
                </span>
              )}
            </button>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderTop: "1px solid var(--bd)",
            }}
          >
            {pane === "feed" ? Feed : pane === "spine" ? Spine : Right}
          </div>
        </>
      ) : (
        <div className={"simwrap" + (showForecast ? " forecast-on" : "")}>
          {Feed}
          {Spine}
          {Right}
          {showForecast && (
            <ForecastPanel
              comparison={forecastComparison}
              forecasts={forecasts}
              selectedForecastId={selForecastId}
              setSelectedForecastId={setSelForecastId}
              emailId={feedOpen}
              totalEmails={total}
              processedCount={processed.length}
              computing={forecastComputing}
              onRecompute={() => void recomputeForecast()}
              onHide={() => setShowForecast(false)}
              onUpload={matterId ? () => setForecastUploadOpen(true) : undefined}
              onDelete={onForecastDelete}
            />
          )}
        </div>
      )}
      {forecastUploadOpen && matterId && (
        <ForecastUploadModal
          matterId={matterId}
          mattersInScope={mattersInScope}
          onClose={() => setForecastUploadOpen(false)}
          onUploaded={onForecastUploaded}
        />
      )}
    </main>
  );
}
